import { useState, useEffect, useCallback } from "react";
import { ethers } from "ethers";
import { CONTRACT_ADDRESSES } from "../contracts/addresses";
import { useEthers } from "./useEthers";
import VestingAbi from "../contracts/abis/VestingAbi.json";

export function useVestingContract() {
  const { provider, account, getContract } = useEthers();
  
  const [contract, setContract] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [isClaiming, setIsClaiming] = useState(false);
  
  // Estados para almacenar datos del contrato
  const [developerInfo, setDeveloperInfo] = useState(null);
  const [vestingStart, setVestingStart] = useState(null);
  const [vestingDuration, setVestingDuration] = useState(null);
  const [vestingActive, setVestingActive] = useState(false);
  const [cliffPeriod, setCliffPeriod] = useState(null);
  const [nextRelease, setNextRelease] = useState(null);
  const [nextReleaseAmount, setNextReleaseAmount] = useState(null);
  
  // Estados calculados para UI
  const [unlockedTokens, setUnlockedTokens] = useState("0");
  const [vestingInfo, setVestingInfo] = useState({
    totalAmount: "0",
    claimedAmount: "0",
    remainingAmount: "0",
    startDate: null,
    endDate: null,
    nextUnlock: null,
    progressPercent: 0,
  });
  
  // Inicializar contrato
  useEffect(() => {
    if (!provider) return;
    
    try {
      const vestingContract = getContract(CONTRACT_ADDRESSES.vesting, VestingAbi);
      setContract(vestingContract);
    } catch (error) {
      console.error("Error al inicializar contrato de vesting:", error);
    }
  }, [provider, getContract]);
  
  // Cargar datos del contrato
  useEffect(() => {
    const loadVestingData = async () => {
      if (!contract || !account) {
        setIsLoading(false);
        return;
      }
      
      try {
        setIsLoading(true);
        
        // Cargar datos en paralelo para optimizar
        const [
          devInfo,
          vestingStartTime,
          vestingDurationTime,
          vestingActiveStatus,
          cliffPeriodTime
        ] = await Promise.all([
          contract.developers(account),
          contract.vestingStartTimestamp(),
          contract.vestingDuration ? contract.vestingDuration() : Promise.resolve(ethers.BigNumber.from(0)),
          contract.vestingActive ? contract.vestingActive() : Promise.resolve(false),
          contract.CLIFF_PERIOD()
        ]);
        
        // Actualizar estados
        setDeveloperInfo(devInfo);
        setVestingStart(vestingStartTime);
        setVestingDuration(vestingDurationTime);
        setVestingActive(vestingActiveStatus);
        setCliffPeriod(cliffPeriodTime);
        
        // Si el desarrollador está activo, cargar datos adicionales
        if (devInfo && devInfo.isActive) {
          const [nextReleaseDate, nextAmount] = await Promise.all([
            contract.getNextReleaseDate ? contract.getNextReleaseDate(account) : Promise.resolve(ethers.BigNumber.from(0)),
            contract.getNextReleaseAmount ? contract.getNextReleaseAmount(account) : Promise.resolve(ethers.BigNumber.from(0))
          ]);
          
          setNextRelease(nextReleaseDate);
          setNextReleaseAmount(nextAmount);
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error("Error al cargar datos de vesting:", error);
        setIsLoading(false);
      }
    };
    
    loadVestingData();
    
    // Actualizar cada minuto
    const interval = setInterval(loadVestingData, 60000);
    return () => clearInterval(interval);
  }, [contract, account]);
  
  // Calcular información del vesting
  useEffect(() => {
    const calculateVestingInfo = async () => {
      if (!account || !contract || !developerInfo || !developerInfo.isActive) {
        setVestingInfo({
          totalAmount: "0",
          claimedAmount: "0",
          remainingAmount: "0",
          startDate: null,
          endDate: null,
          nextUnlock: null,
          progressPercent: 0,
        });
        setUnlockedTokens("0");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        // Formatear datos del desarrollador
        const totalAmount = formatBigNumber(developerInfo.totalAmount);
        const claimedAmount = formatBigNumber(developerInfo.claimedAmount);
        const remainingAmount = (parseFloat(totalAmount) - parseFloat(claimedAmount)).toString();

        // Calcular tokens desbloqueados disponibles para reclamar
        const unlocked = await getUnlockedTokens();

        // Calcular fechas importantes
        const startTime = vestingStart ? new Date(vestingStart.toNumber() * 1000) : null;
        const endTime = startTime && vestingDuration ? 
                      new Date((vestingStart.toNumber() + vestingDuration.toNumber()) * 1000) : 
                      null;
        
        // Calcular próxima fecha de liberación
        let nextUnlockDate = null;
        if (nextRelease && nextRelease.gt(0)) {
          nextUnlockDate = new Date(nextRelease.toNumber() * 1000);
        } else if (startTime && cliffPeriod) {
          // Fallback manual si la función del contrato no devuelve un valor válido
          const cliffEnd = new Date(startTime.getTime() + cliffPeriod.toNumber() * 1000);
          if (cliffEnd > new Date()) {
            nextUnlockDate = cliffEnd;
          } else {
            // Calcular próxima liberación semanal
            const now = new Date();
            const weekInMs = 7 * 24 * 60 * 60 * 1000;
            const weeksSinceCliffEnd = Math.floor((now - cliffEnd) / weekInMs);
            nextUnlockDate = new Date(cliffEnd.getTime() + (weeksSinceCliffEnd + 1) * weekInMs);
          }
        }

        // Calcular progreso del vesting
        let progressPercent = 0;
        if (startTime && endTime) {
          const totalDuration = endTime - startTime;
          const elapsedTime = Math.min(Date.now() - startTime, totalDuration);
          progressPercent = Math.floor((elapsedTime / totalDuration) * 100);
        }

        setVestingInfo({
          totalAmount,
          claimedAmount,
          remainingAmount,
          startDate: startTime,
          endDate: endTime,
          nextUnlock: nextUnlockDate,
          progressPercent,
        });

        setUnlockedTokens(unlocked);
        setIsLoading(false);
        setLastUpdateTime(new Date());
      } catch (error) {
        console.error("Error al calcular información de vesting:", error);
        setIsLoading(false);
      }
    };

    calculateVestingInfo();
    
    // Actualizar cada minuto
    const interval = setInterval(calculateVestingInfo, 60000);
    return () => clearInterval(interval);
  }, [account, contract, developerInfo, vestingStart, vestingDuration, cliffPeriod, nextRelease]);
  
  // Obtener tokens desbloqueados
  const getUnlockedTokens = async () => {
    if (!account || !contract || !developerInfo || !developerInfo.isActive) return "0";
    
    try {
      // Usar calculateClaimableTokens del contrato
      const unlocked = await contract.calculateClaimableTokens(account);
      return formatBigNumber(unlocked);
    } catch (error) {
      console.error("Error al obtener tokens desbloqueados:", error);
      
      // Fallback manual si la función del contrato falla
      try {
        const released = await contract.calculateReleasedTokens(account);
        const claimed = developerInfo.claimedAmount;
        const remaining = ethers.BigNumber.from(released).sub(claimed);
        if (remaining.lte(0)) return "0";
        return formatBigNumber(remaining);
      } catch (innerError) {
        console.error("Error en cálculo fallback:", innerError);
        return "0";
      }
    }
  };
  
  // Formatear valores para mostrar
  const formatBigNumber = (bn) => {
    if (!bn) return "0";
    try {
      return ethers.utils.formatEther(bn);
    } catch (error) {
      console.error("Error formateando BigNumber:", error);
      return "0";
    }
  };
  
  // Función para refrescar datos
  const refreshData = useCallback(async () => {
    if (!contract || !account) return;
    
    try {
      // Cargar datos del desarrollador
      const devInfo = await contract.developers(account);
      setDeveloperInfo(devInfo);
      
      // Cargar datos de liberación
      if (contract.getNextReleaseDate) {
        const nextReleaseDate = await contract.getNextReleaseDate(account);
        setNextRelease(nextReleaseDate);
      }
      
      if (contract.getNextReleaseAmount) {
        const nextAmount = await contract.getNextReleaseAmount(account);
        setNextReleaseAmount(nextAmount);
      }
      
      const unlocked = await getUnlockedTokens();
      setUnlockedTokens(unlocked);
      setLastUpdateTime(new Date());
    } catch (error) {
      console.error("Error al refrescar datos de vesting:", error);
    }
  }, [contract, account]);
  
  // Reclamar tokens liberados
  const claimTokens = useCallback(async () => {
    if (!contract || !account) {
      throw new Error("Contrato no disponible o wallet no conectada");
    }
    
    if (!developerInfo || !developerInfo.isActive) {
      throw new Error("No tienes participación en el vesting");
    }
    
    try {
      setIsClaiming(true);
      
      // Llamar a la función claimTokens del contrato
      const tx = await contract.claimTokens();
      const receipt = await tx.wait();
      
      // Refrescar datos
      await refreshData();
      
      setIsClaiming(false);
      return receipt;
    } catch (error) {
      setIsClaiming(false);
      console.error("Error al reclamar tokens:", error);
      throw error;
    }
  }, [contract, account, developerInfo, refreshData]);

  // Formatear fecha para mostrar
  const formatDate = (date) => {
    if (!date) return "No disponible";
    return date.toLocaleDateString("es-ES", {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Verificar si el usuario tiene vesting
  const hasVesting = developerInfo && developerInfo.isActive;

  return {
    contract,
    hasVesting,
    developerInfo,
    vestingInfo,
    unlockedTokens,
    vestingActive: vestingActive || false,
    vestingStartDate: vestingStart ? new Date(vestingStart.toNumber() * 1000) : null,
    vestingEndDate: vestingStart && vestingDuration ? 
                   new Date((vestingStart.toNumber() + vestingDuration.toNumber()) * 1000) : 
                   null,
    cliffEndDate: vestingStart && cliffPeriod ? 
                 new Date((vestingStart.toNumber() + cliffPeriod.toNumber()) * 1000) : 
                 null,
    formatDate,
    claimTokens,
    isClaiming,
    isLoading,
    refreshData,
    lastUpdateTime
  };
}
